import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import type { EventItem } from '../../types';
import { coalescePriceFromApiAndRawTiers } from '../../utils/eventMedia';
import { getEventBookingWindowStatus } from '../../utils/eventBookingWindow';
import { dayOfMonth, formatDayShort, formatTime12, monthShort, relativeDay } from '../../utils/eventDates';
import { categoryMeta } from '../../utils/eventCategories';
import type { Announcement } from '../../api/announcementsApi';
import './home.css';
import { Icon } from '../../components/icons/SpriteIcons';
import { ShareButton } from '../../components/events/ShareButton';

/* ---------- poster ---------- */

interface PosterProps {
  event: EventItem;
  eager?: boolean;
  /** Art only, no text - used behind overlaid copy. */
  bare?: boolean;
}

const EventPosterBase: React.FC<PosterProps> = ({ event, eager = false }) => {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const showImage = Boolean(event.image) && !failed;

  useEffect(() => {
    setFailed(false);
    setLoaded(false);
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0) setLoaded(true);
  }, [event.image]);

  return (
    <div className="poster">
      <div className="poster-empty" aria-hidden="true">
        <Icon name="i-ticket" size={28} />
      </div>
      {showImage ? (
        <img
          ref={imgRef}
          src={event.image}
          alt={`${event.title} poster`}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          className={loaded ? 'ok' : undefined}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
        />
      ) : null}
    </div>
  );
};

/* ---------- card ---------- */

export const formatPrice = (n: number) => (n === 0 ? 'Free' : `₹${Number(n).toLocaleString('en-IN')}`);

export const eventMinPrice = (event: EventItem) =>
  coalescePriceFromApiAndRawTiers(event.priceFrom, (event.tiers ?? []) as unknown[]);

const lowStock = (event: EventItem): number | null => {
  const n = event.availableTickets;
  return typeof n === 'number' && n > 0 && n <= 20 ? n : null;
};

const HomeEventCardBase: React.FC<{ event: EventItem }> = ({ event }) => {
  const meta = categoryMeta(event.category);
  const status = getEventBookingWindowStatus(event);
  const open = status === 'open';
  const min = eventMinPrice(event);
  const low = open ? lowStock(event) : null;

  return (
    <article className="ecard">
      <div className="media">
        <EventPoster event={event} />
        <div className="badges">
          <div className="date-badge" aria-hidden="true">
            <span className="m">{monthShort(event.date)}</span>
            <span className="d num">{dayOfMonth(event.date)}</span>
          </div>
          <div className="tagcol">
            <span className="tag">{meta.label}</span>
          </div>
        </div>
      </div>
      <div className="body">
        <div className="flex items-start justify-between gap-2">
          <h3 className="title">
            <Link to={`/events/${event.slug}`}>{event.title}</Link>
          </h3>
          <ShareButton event={event} variant="icon" />
        </div>
        <p className="where">
          <Icon name="i-pin" size={14} />
          {event.venue}
          {event.city ? `, ${event.city}` : ''}
        </p>
        <p className="where">
          <Icon name="i-clock" size={14} />
          {relativeDay(event.date)}
          {event.time ? ` · ${formatTime12(event.time)}` : ''}
        </p>
        <div className="foot">
          <div className="price">
            <small>{min === 0 ? 'Entry' : 'From'}</small>
            <b className="num">{formatPrice(min)}</b>
          </div>
          {low != null ? <span className="low-stock">Only {low} left</span> : null}
          {status === 'closed' ? <span className="ended">Sales ended</span> : null}
          {status === 'not_yet_open' ? <span className="ended">On sale soon</span> : null}
          <Link
            className={`btn btn-sm cta ${open ? 'btn-primary' : 'btn-ghost'}`}
            to={`/events/${event.slug}`}
            aria-label={`${open ? 'Book' : 'View'} ${event.title}`}
          >
            {open ? 'Book' : 'View'}
          </Link>
        </div>
      </div>
    </article>
  );
};

export const CardSkeletons: React.FC<{ count: number }> = ({ count }) => (
  <>
    {Array.from({ length: count }, (_, i) => (
      <div className="sk" aria-hidden="true" key={i}>
        <div className="sk-media" />
        <div className="sk-line" />
        <div className="sk-line s" />
      </div>
    ))}
  </>
);

/* ---------- section head + state panels ---------- */

export const SecHead: React.FC<{ eyebrow: string; title: React.ReactNode; id?: string; right?: React.ReactNode }> = ({
  eyebrow,
  title,
  id,
  right,
}) => (
  <div className="sec-head">
    <div className="stack">
      <span className="eyebrow">{eyebrow}</span>
      <h2 className="zq-h2" id={id}>
        {title}
      </h2>
    </div>
    {right}
  </div>
);

export const StateBlock: React.FC<{
  tone?: 'empty' | 'error';
  icon: string;
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ tone = 'empty', icon, title, actions, children }) => (
  <div className={`state${tone === 'error' ? ' error' : ''}`} role={tone === 'error' ? 'alert' : 'status'}>
    <div className="ico" style={tone === 'error' ? { color: 'var(--rose)' } : undefined}>
      <Icon name={icon} size={24} />
    </div>
    <h3>{title}</h3>
    <p>{children}</p>
    {actions ? <div className="row">{actions}</div> : null}
  </div>
);

/* ---------- coming-soon posters (admin-uploaded, not ticketed) ---------- */

/** Full-size poster viewer; closes on Esc, backdrop click or the close button. */
const PosterLightbox: React.FC<{ item: Announcement; onClose: () => void }> = ({ item, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return createPortal(
    <div className="poster-lightbox" role="dialog" aria-modal="true" aria-label={item.name} onClick={onClose}>
      <button type="button" className="lb-close" aria-label="Close" onClick={onClose} autoFocus>
        <Icon name="i-x" size={22} />
      </button>
      <figure onClick={(e) => e.stopPropagation()}>
        <img src={item.posterLargeUrl} alt={`${item.name} poster`} />
        <figcaption>
          <b>{item.name}</b>
          <span>
            {formatDayShort(item.eventDate)} · {item.city}
          </span>
        </figcaption>
      </figure>
    </div>,
    document.body
  );
};

const PromoCardBase: React.FC<{ promo: Announcement; eager?: boolean }> = ({ promo, eager = false }) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const when = formatDayShort(promo.eventDate);
  return (
    <>
      <button type="button" className="promo-card" onClick={() => setOpen(true)} aria-label={`View poster: ${promo.name}, ${when}, ${promo.city}`}>
        <div className="media">
          <img src={promo.posterUrl} alt={`${promo.name} poster`} loading={eager ? 'eager' : 'lazy'} decoding="async" />
          <span className="promo-tag">Coming soon</span>
        </div>
        <div className="promo-meta">
          <b>{promo.name}</b>
          <span>
            {when} · {promo.city}
          </span>
        </div>
      </button>
      {open ? <PosterLightbox item={promo} onClose={close} /> : null}
    </>
  );
};

/** The "smoky" 4th slot: blurred posters behind a Check out all button. */
export const PromoSmokeTile: React.FC<{ promos: Announcement[]; total: number }> = ({ promos, total }) => (
  <Link className="promo-smoke" to="/upcoming" aria-label={`Check out all ${total} upcoming events`}>
    <div className="smoke-bg" aria-hidden="true">
      {promos.slice(0, 3).map((p) => (
        <div key={p.id} className="smoke-p">
          <img src={p.posterUrl} alt="" loading="lazy" decoding="async" />
        </div>
      ))}
    </div>
    <div className="smoke-fog" aria-hidden="true" />
    <div className="smoke-copy">
      <span className="eyebrow">{total} coming up</span>
      <span className="btn btn-primary btn-lg">
        Check out all <Icon name="i-arrow" />
      </span>
    </div>
  </Link>
);

export const EventPoster = React.memo(EventPosterBase);
export const HomeEventCard = React.memo(HomeEventCardBase);
export const PromoCard = React.memo(PromoCardBase);
